import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Page, PageHeader, PageContent } from '@supabank/ui';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';

export interface FormPageProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  onBack?: () => void;
  returnPath?: string;
}

/**
 * FormPage - Universal full-screen form page component
 * 
 * Features:
 * - Back button in header (navigates to returnPath or history.back())
 * - Return path saved in location.state
 * - Uses Page component from Design Canon
 * - Mobile-first responsive design
 * 
 * @example
 * <FormPage
 *   icon={CreditCard}
 *   title="Deposit Funds"
 *   subtitle="Add funds to your wallet"
 *   returnPath="/dashboard"
 * >
 *   <form>...</form>
 * </FormPage>
 */
export function FormPage({
  icon: Icon,
  title,
  subtitle,
  children,
  className,
  onBack,
  returnPath,
}: FormPageProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Get return path from location.state or use provided returnPath
  const getReturnPath = (): string | null => {
    if (onBack) return null; // Custom handler takes precedence
    
    // Check location.state first (set when navigating TO this page)
    const state = location.state as { returnPath?: string } | null;
    if (state?.returnPath) {
      return state.returnPath;
    }
    
    // Fallback to provided returnPath prop
    if (returnPath) {
      return returnPath;
    }
    
    return null;
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    const path = getReturnPath();
    if (path) {
      navigate(path);
      return;
    }

    // Fallback to browser back
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }

    // Ultimate fallback: navigate to dashboard
    navigate('/dashboard');
  };

  return (
    <Page data-ux-contract="canon" className={cn('min-h-screen', className)}>
      {/* Custom Header with Back Button */}
      <header
        className={cn(
          'mb-8',
          semanticTokens.motion.slideDown,
          semanticTokens.motion.reduced
        )}
      >
        {/* Back Button */}
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className={cn(
              'gap-2 min-h-[44px]',
              semanticTokens.motion.standard,
              semanticTokens.focus.ring
            )}
            aria-label="Go back"
          >
            <SFIcon icon={sfIcons.back} className="w-4 h-4" aria-hidden="true" />
            Back
          </Button>
        </div>

        {/* Title Section */}
        <div className="flex items-start gap-4">
          {Icon && (
            <div className={cn('mt-1', semanticTokens.fg.muted)}>
              <Icon className="h-6 w-6" />
            </div>
          )}
          <div className="flex-1">
            <h1 className={cn(semanticTokens.typography.h1, semanticTokens.fg.default)}>
              {title}
            </h1>
            {subtitle && (
              <p className={cn('mt-2', semanticTokens.typography.body, semanticTokens.fg.muted)}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </header>

      <PageContent>
        {children}
      </PageContent>
    </Page>
  );
}

